import React, { useEffect, useState, useMemo } from "react";
import { MessageSquare, Smile, Meh, Frown } from "lucide-react";
import { api } from "../lib/api.js";

const SURVEY_TYPES = ["", "NPS", "CSI", "CES"];
const DEPARTMENTS = ["", "Vendas", "Workshop", "BPS", "Reclamações", "Peças"];

export default function SurveyAnalyticsPage() {
  const [data, setData] = useState(null);
  const [dept, setDept] = useState("");
  const [stype, setStype] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ top_n: "40" });
      if (dept) q.append("department", dept);
      if (stype) q.append("survey_type", stype);
      const { data } = await api.get(`/surveys/analytics/keywords?${q}`);
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [dept, stype]);

  // Compute font size scale for word cloud
  const maxCount = useMemo(() => Math.max(1, ...((data?.keywords || []).map((k) => k.count))), [data]);
  const sentiments = data?.sentiments || { positive: 0, neutral: 0, negative: 0 };
  const totalSent = sentiments.positive + sentiments.neutral + sentiments.negative;

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1A1A2E] flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-[#1F3864]" /> Análise de Respostas de Satisfação
          </h1>
          <p className="text-sm text-[#6C757D] mt-1">Palavras-chave frequentes e sentimento inferido dos comentários dos clientes.</p>
        </div>
        <div className="flex gap-2">
          <select value={dept} onChange={(e) => setDept(e.target.value)} className="text-xs border border-gray-300 rounded-sm px-2 py-1.5" data-testid="analytics-dept">
            {DEPARTMENTS.map((d) => <option key={d} value={d}>{d || "Todos departamentos"}</option>)}
          </select>
          <select value={stype} onChange={(e) => setStype(e.target.value)} className="text-xs border border-gray-300 rounded-sm px-2 py-1.5" data-testid="analytics-type">
            {SURVEY_TYPES.map((t) => <option key={t} value={t}>{t || "Todos tipos"}</option>)}
          </select>
        </div>
      </div>

      {loading && <div className="text-sm text-[#6C757D] italic">A analisar…</div>}

      {data && (
        <>
          {/* Sentiments bar */}
          <div className="bg-white border border-gray-200 rounded-md p-4 mb-4" data-testid="analytics-sentiments">
            <div className="font-heading text-sm font-bold text-[#1A1A2E] mb-3 uppercase tracking-widest">Sentimento inferido · {data.total_comments} comentários</div>
            {totalSent === 0 ? (
              <div className="text-sm text-[#6C757D] italic">Sem comentários no período.</div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-4 mb-3">
                  <SentimentCard icon={Smile} label="Positivo" value={sentiments.positive} total={totalSent} color="#28A745" />
                  <SentimentCard icon={Meh} label="Neutro" value={sentiments.neutral} total={totalSent} color="#DC7633" />
                  <SentimentCard icon={Frown} label="Negativo" value={sentiments.negative} total={totalSent} color="#DC3545" />
                </div>
                {/* Stacked bar */}
                <div className="flex h-3 rounded-sm overflow-hidden border border-gray-200">
                  <div style={{ width: `${(sentiments.positive / totalSent) * 100}%`, background: "#28A745" }} />
                  <div style={{ width: `${(sentiments.neutral / totalSent) * 100}%`, background: "#DC7633" }} />
                  <div style={{ width: `${(sentiments.negative / totalSent) * 100}%`, background: "#DC3545" }} />
                </div>
              </>
            )}
          </div>

          {/* Keyword cloud */}
          <div className="bg-white border border-gray-200 rounded-md p-4 mb-4" data-testid="analytics-cloud">
            <div className="font-heading text-sm font-bold text-[#1A1A2E] mb-3 uppercase tracking-widest">Palavras-chave frequentes</div>
            {(data.keywords || []).length === 0 ? (
              <div className="text-sm text-[#6C757D] italic text-center py-6">Nenhuma palavra encontrada nos comentários.</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {data.keywords.map((k) => {
                  const scale = 0.75 + (k.count / maxCount) * 1.5;
                  return (
                    <span
                      key={k.word}
                      className="inline-flex items-baseline gap-1 bg-[#F4F6F8] border border-gray-200 rounded-sm px-2 py-1 cursor-help hover:border-[#1F3864] transition"
                      style={{ fontSize: `${scale}rem` }}
                      title={k.sample}
                      data-testid={`analytics-kw-${k.word}`}
                    >
                      {k.word}
                      <span className="text-[10px] font-mono text-[#6C757D]">{k.count}</span>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Keyword table with samples */}
          <div className="bg-white border border-gray-200 rounded-md p-4">
            <div className="font-heading text-sm font-bold text-[#1A1A2E] mb-3 uppercase tracking-widest">Top termos · exemplo de contexto</div>
            {(data.keywords || []).length === 0 ? (
              <div className="text-sm text-[#6C757D] italic text-center py-6">—</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-[#6C757D] uppercase tracking-widest text-[10px]">
                    <th className="text-left py-1.5 px-2">Palavra</th>
                    <th className="text-right py-1.5 px-2">Ocorrências</th>
                    <th className="text-left py-1.5 px-2">Exemplo</th>
                  </tr>
                </thead>
                <tbody>
                  {data.keywords.slice(0, 15).map((k) => (
                    <tr key={k.word} className="border-b border-gray-100">
                      <td className="py-1.5 px-2 font-semibold">{k.word}</td>
                      <td className="py-1.5 px-2 text-right font-mono">{k.count}</td>
                      <td className="py-1.5 px-2 text-xs text-[#6C757D] italic truncate max-w-md" title={k.sample}>"{k.sample}"</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SentimentCard({ icon: Icon, label, value, total, color }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div className="text-center">
      <Icon className="w-6 h-6 mx-auto mb-1" style={{ color }} />
      <div className="font-heading text-2xl font-bold" style={{ color }}>{pct}%</div>
      <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label} · {value}</div>
    </div>
  );
}
